/* eslint no-console: "off" */
import type { LogAttributes } from "@opentelemetry/api-logs";
import pino from "pino";

const PINO_LEVEL_TO_OTEL: Record<string, string> = {
  10: "trace",
  20: "debug",
  30: "info",
  40: "warn",
  50: "error",
  60: "fatal",
};

/**
 * Extract body and attributes from pino's two calling forms.
 *
 * @example
 * parsePinoArgs(["Form submitted"])
 * // → { body: "Form submitted", attributes: undefined }
 *
 * @example
 * parsePinoArgs([{ formId: "abc", email: "a@b.com" }, "Form submitted"])
 * // → { body: "Form submitted", attributes: { formId: "abc", email: "a@b.com" } }
 */
export function parsePinoArgs(
  inputArgs: Parameters<pino.LogFn>,
  bindings?: LogAttributes
) {
  const [first, second] = inputArgs;
  const hasAttrs = typeof first === "object" && first !== null;
  return {
    body: String(
      hasAttrs
        ? (second ?? (first instanceof Error ? first.message : ""))
        : (first ?? "")
    ),
    attributes: bindings
      ? { ...bindings, ...(hasAttrs ? (first as LogAttributes) : {}) }
      : hasAttrs
        ? (first as LogAttributes)
        : undefined,
  };
}

export default pino({
  hooks: {
    logMethod(inputArgs, method, level) {
      if (typeof window === "undefined") {
        import("@/lib/otel-logger")
          .then(({ logger: otelLogger }) =>
            otelLogger.emit({
              level: PINO_LEVEL_TO_OTEL[String(level)] ?? "info",
              ...parsePinoArgs(inputArgs, this.bindings()),
            })
          )
          .catch(() => {
            // Logging failures must never crash the application
          });
      }
      method.apply(this, inputArgs);
    },
  },
  browser: {
    write: {
      error: (obj) => {
        window?.DD_LOGS?.logger?.error(
          (obj as object & { msg: string }).msg,
          obj
        );
        console.error(obj);
      },
      warn: (obj) => {
        window?.DD_LOGS?.logger?.warn(
          (obj as object & { msg: string }).msg,
          obj
        );
        console.warn(obj);
      },
      info: (obj) => {
        console.log(obj);
      },
    },
  },
});
